/**
 * Collapsible tool call card — shows tool name collapsed,
 * entity cards (messages, contacts, etc.) when expanded.
 */

import { useState } from "react";
import type { JSX } from "react";
import { Icon } from "../ui/Icon";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { extractEntities, ToolResultEntityCards } from "./AgentPanelBlocks";
import {
  humanizeToolName,
  extractResultCountLabel,
  extractArgSummary,
  toolResultError,
} from "./toolCallHelpers";

export interface ToolCallCardProps {
  readonly name: string;
  readonly args?: unknown;
  readonly result?: unknown;
  readonly status: "pending" | "complete" | "error";
  readonly runtime?: AppRuntime;
}

function StatusIcon({ status }: { readonly status: ToolCallCardProps["status"] }): JSX.Element {
  switch (status) {
    case "pending":
      return (
        <span className="text-agent-text-muted animate-spin">
          <Icon name="loader" size={13} />
        </span>
      );
    case "complete":
      return (
        <span className="text-emerald-400">
          <Icon name="circle-check" size={13} />
        </span>
      );
    case "error":
      return (
        <span className="text-red-400">
          <Icon name="circle-alert" size={13} />
        </span>
      );
  }
}

export function ToolCallCard({
  name,
  args,
  result,
  status,
  runtime,
}: ToolCallCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const label = humanizeToolName(name);
  const summary = extractArgSummary(args);
  const errorMessage = status === "error" ? toolResultError(result) : null;
  const count = errorMessage ? null : extractResultCountLabel(result);
  const entities = result && !errorMessage ? extractEntities(result) : [];
  const hasContent = entities.length > 0;

  return (
    <div className="rounded-lg bg-surface-tertiary border border-edge text-[12px]">
      {/* Collapsed header — always visible */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:brightness-110 transition-all"
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick={hasContent ? () => { setExpanded(!expanded); } : undefined}
      >
        <span className="shrink-0 flex items-center translate-y-[3px]">
          <StatusIcon status={status} />
        </span>
        <span className="flex-1 min-w-0 truncate text-agent-text">
          <span className="font-bold">{label}</span>
          {count != null && (
            <span className="font-normal text-agent-text-dim ml-1">
              ({count})
            </span>
          )}
          {summary && (
            <span className="font-normal text-agent-text-muted ml-1">
              : {summary}
            </span>
          )}
        </span>
        {hasContent && (
          <span className="shrink-0 flex items-center text-agent-text-dim">
            <Icon name={expanded ? "chevron-down" : "chevron-right"} size={12} />
          </span>
        )}
      </button>

      {/* Failed tool — terminal error message, never a spinner. */}
      {errorMessage != null && (
        <div className="px-3 pb-2 text-[12px] text-red-400 break-words">
          {errorMessage}
        </div>
      )}

      {/* Expanded body — entity cards */}
      {expanded && hasContent && runtime && (
        <div className="px-3 pb-3">
          <ToolResultEntityCards toolName={name} result={result} runtime={runtime} />
        </div>
      )}
    </div>
  );
}
